import { Box, Button, Stack, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { useTranslatedText } from 'hooks/useTranslatedText';

import BorderStack from 'components/@common/BorderStack';
import Icon from 'components/@icons/Icon';
import { SelectedSeat } from 'components/@seating/types';
import TicketPrice from 'components/@tickets/TicketPrice';
import TicketQuantityIcon from 'components/@tickets/TicketQuantityIcon';

interface Props {
  seat: SelectedSeat;
  onRemoveSeat: (seat: SelectedSeat) => void;
}

const Seat = ({ seat, onRemoveSeat }: Props) => {
  const intl = useIntl();
  const localize = useTranslatedText();

  const seatType = (seat.displayObjectType ?? seat.objectType).toLowerCase();

  return (
    <BorderStack
      key={seat.uuid}
      sx={{
        px: 2,
        py: 1.5,
        p: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          p: 2,
        }}
      >
        <TicketQuantityIcon seatColor={seat.category?.color} />
        <Stack
          sx={{
            flex: 1,
          }}
        >
          <Typography
            variant="small"
            sx={{
              fontWeight: 'bold',
            }}
          >
            {localize(seat.name)}
          </Typography>
          <TicketPrice
            // @ts-expect-error don't care about this
            discountedPrice={seat.pricing.price}
            originalPrice={seat.pricing.originalPrice}
            color="text.secondary"
          />
        </Stack>

        <Button sx={{ alignSelf: 'center' }} onClick={() => onRemoveSeat(seat)}>
          <Icon name="Trash" />
        </Button>
      </Box>
      <Box
        sx={{
          display: 'flex',
          p: 2,
          borderTop: '1px solid',
          borderColor: 'grey.800',
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
        }}
      >
        <Stack
          spacing={0.5}
          sx={{
            flex: 1,
          }}
        >
          <Typography
            variant="extraSmall"
            sx={{
              fontWeight: 500,
              color: 'grey.500',
            }}
          >
            <FormattedMessage id="seat_section.label" />
          </Typography>
          <Typography
            variant="extraSmall"
            sx={{
              fontWeight: 'bold',
            }}
          >
            {seat.labels.section}{' '}
            {'numSelected' in seat && typeof seat.numSelected === 'number' && (
              <>({seat.numSelected}x)</>
            )}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            flex: 1,
          }}
        >
          {seat.labels.parent && (
            <Stack
              spacing={0.5}
              sx={{
                flexGrow: 1,
              }}
            >
              <Typography
                variant="extraSmall"
                sx={{
                  fontWeight: 500,
                  color: 'grey.500',
                }}
              >
                <FormattedMessage id="seat_parent.label" />
              </Typography>
              <Typography
                variant="extraSmall"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {seat.labels.parent}
              </Typography>
            </Stack>
          )}

          <Stack
            spacing={0.5}
            sx={{
              flexGrow: 1,
            }}
          >
            <Typography
              variant="extraSmall"
              sx={{
                fontWeight: 500,
                color: 'grey.500',
              }}
            >
              <FormattedMessage
                id={`seat_type.${seatType}`}
                defaultMessage={intl.formatMessage({ id: 'seat_type.seat' })}
              />
            </Typography>
            <Typography
              variant="extraSmall"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {seat.labels.own}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </BorderStack>
  );
};

export default Seat;
